import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Pause, Play, X } from 'lucide-react';

import { Button } from '../ui/button';
import { formatDateHeure } from '../../lib/formats';
import { horodatagePoint } from '../../lib/traceGps';
import { prefereMoinsDeMouvement } from './markerMotion';

/** Combien de secondes du trajet passent à chaque seconde réelle. */
export const VITESSES_LECTURE = [10, 60, 300];
const PAS_MS = 100;
const PAS_MS_SOBRE = 400;

/** Le rang du dernier relevé daté au plus tard à `t` (en ms depuis le départ). */
const rangA = (temps, t) => {
  let bas = 0;
  let haut = temps.length - 1;
  while (bas < haut) {
    const milieu = Math.ceil((bas + haut) / 2);
    if (temps[milieu] <= t) bas = milieu;
    else haut = milieu - 1;
  }
  return bas;
};

/**
 * Barre de lecture d'un trajet: lecture/pause, vitesse (×10, ×60, ×300 le
 * temps réel), curseur sur la durée du trajet, heure du relevé en cours.
 * Ne dessine rien sur la carte: elle annonce le relevé en cours via
 * `onPosition(rang)`, la page déplace le camion.
 */
export default function LecteurTrajet({ points, onPosition, onFermer }) {
  const temps = useMemo(() => {
    const brut = (points || []).map(horodatagePoint);
    const debut = brut.find((t) => !Number.isNaN(t)) ?? 0;
    // Un relevé sans heure reste au même instant que le précédent.
    let precedent = 0;
    return brut.map((t) => {
      const relatif = Number.isNaN(t) ? precedent : Math.max(precedent, t - debut);
      precedent = relatif;
      return relatif;
    });
  }, [points]);
  const duree = temps.length ? temps[temps.length - 1] : 0;

  const [t, setT] = useState(0);
  const [lecture, setLecture] = useState(false);
  const [vitesse, setVitesse] = useState(VITESSES_LECTURE[1]);

  const rang = temps.length ? rangA(temps, t) : 0;

  const onPositionRef = useRef(onPosition);
  onPositionRef.current = onPosition;
  useEffect(() => {
    if (onPositionRef.current) onPositionRef.current(rang);
  }, [rang]);

  // Les mouvements réduits ne suppriment rien: la lecture avance par pas plus
  // espacés, donc moins de sauts à l'écran.
  const pas = prefereMoinsDeMouvement() ? PAS_MS_SOBRE : PAS_MS;
  useEffect(() => {
    if (!lecture) return undefined;
    let dernier = Date.now();
    const minuteur = setInterval(() => {
      const maintenant = Date.now();
      const ecoule = maintenant - dernier;
      dernier = maintenant;
      setT((courant) => Math.min(duree, courant + ecoule * vitesse));
    }, pas);
    return () => clearInterval(minuteur);
  }, [lecture, vitesse, duree, pas]);

  useEffect(() => {
    if (lecture && t >= duree) setLecture(false);
  }, [lecture, t, duree]);

  const basculer = useCallback(() => {
    if (lecture) {
      setLecture(false);
      return;
    }
    if (t >= duree) setT(0);
    setLecture(true);
  }, [lecture, t, duree]);

  const point = points?.[rang];

  return (
    <div
      data-testid="lecteur-trajet"
      className="absolute inset-x-2 bottom-2 z-[400] rounded-lg border border-border bg-card/95 p-2 text-card-foreground shadow-md backdrop-blur"
    >
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant="flame"
          onClick={basculer}
          aria-label={lecture ? 'Pause' : 'Lecture'}
          data-testid="lecteur-lecture"
        >
          {lecture ? <Pause aria-hidden="true" /> : <Play aria-hidden="true" />}
        </Button>
        <div role="group" aria-label="Vitesse de lecture" className="flex items-center gap-1">
          {VITESSES_LECTURE.map((valeur) => (
            <Button
              key={valeur}
              type="button"
              size="sm"
              variant={vitesse === valeur ? 'default' : 'outline'}
              aria-pressed={vitesse === valeur}
              onClick={() => setVitesse(valeur)}
              className="px-2"
            >
              ×{valeur}
            </Button>
          ))}
        </div>
        <Button type="button" size="sm" variant="ghost" onClick={onFermer} className="ml-auto">
          <X aria-hidden="true" />
          Fermer
        </Button>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
        <input
          type="range"
          aria-label="Moment du trajet"
          className="h-6 min-w-[8rem] flex-1 accent-[hsl(var(--flame))]"
          min={0}
          max={Math.max(1, Math.round(duree / 1000))}
          step={1}
          value={Math.round(t / 1000)}
          onChange={(event) => setT(Math.min(duree, Number(event.target.value) * 1000))}
        />
        <span className="text-xs font-medium tabular-nums text-muted-foreground" data-testid="lecteur-heure">
          {formatDateHeure(point?.timestamp)}
        </span>
      </div>
    </div>
  );
}
